import { ApiClientError } from "@/lib/api";

/**
 * A failed write turned into per-field messages (API-STANDARD §1 `details`).
 *
 * The case this exists for is the 422 `DATAFLOW_NOT_READY`: the server answers
 * with one detail per offending connection, each naming the connection and what
 * is missing from it. Shown only as a toast, that is a wall of text next to a
 * form the user then has to search; mapped onto `dataflowIds`, it lands beside
 * the list it is about.
 *
 * A detail with no `field` is not dropped — it becomes the form-level message,
 * because an issue the server could not attribute to a field is still the
 * reason the save failed.
 */

export interface ScheduleFieldErrors {
  /** Keyed by the field the server named. */
  fields: Record<string, string>;
  /** Everything the server could not attribute to one field. */
  form: string | null;
  /** The correlation id, which is what support asks for first. */
  reference: string | null;
}

export function scheduleFieldErrors(error: unknown): ScheduleFieldErrors {
  if (!(error instanceof ApiClientError)) {
    return {
      fields: {},
      form: error instanceof Error ? error.message : "The schedule could not be saved.",
      reference: null,
    };
  }

  const fields: Record<string, string> = {};
  const unattributed: string[] = [];
  for (const detail of error.details) {
    if (!detail.field) {
      unattributed.push(detail.issue);
      continue;
    }
    // Several details can name one field — one per not-ready connection — and
    // they are joined rather than overwritten, so a schedule with two draft
    // connections names both instead of only the last.
    fields[detail.field] = fields[detail.field]
      ? `${fields[detail.field]} ${detail.issue}`
      : detail.issue;
  }

  return {
    fields,
    form: unattributed.length > 0 ? unattributed.join(" ") : error.message,
    reference: error.correlationId,
  };
}
